import { useMemo } from "react";

import styled from "styled-components";

import { useNetworkMetadata, usePermissions } from "Flocknote/apiHooks";
import { useIsSuperDuper } from "Flocknote/stateHooks";

import NavLink from "NavBar/components/NavLink";
import NavLinksWrapper from "NavBar/components/NavLinksWrapper";
import NavSection from "NavBar/components/NavSection";
import NavSectionDivider from "NavBar/components/NavSectionDivider";
import useIsSelected from "NavBar/components/useIsSelected";
import {
  navSectionKeys,
  useAddGiftsLinkRef,
  useGivingRef,
  useGivingSummaryRef,
} from "NavBar/navBarStore";
import {
  useCanSeeGiving,
  useIsAddGiftsLinkFlashing,
  useIsGivingSquiggleFlashing,
  useIsGivingSummaryLinkFlashing,
} from "NavBar/stateHooks";

import { navColors } from "UI/colors";

import Funds from "./Funds";
import GivingHeaderBadge from "./GivingHeaderBadge";
import OngoingCampaigns from "./OngoingCampaigns";
import SettingsContent from "./SettingsContent";

const LegacyGiving = ({ canSeeSettings }) => {
  const [{ hasFundIt }] = useNetworkMetadata();

  const [{ canViewAllGivingData, canAddGifts, canSeeCampaignsNav }] =
    usePermissions();
  const settingsSelected = useIsSelected("/giving/settings");
  const isSuperDuper = useIsSuperDuper();
  const givingRef = useGivingRef();
  const givingSummaryRef = useGivingSummaryRef();
  const addGiftsLinkRef = useAddGiftsLinkRef();
  const isAddGiftsFlashing = useIsAddGiftsLinkFlashing();
  const isGivingSummaryFlashing = useIsGivingSummaryLinkFlashing();
  const isGivingSquiggleFlashing = useIsGivingSquiggleFlashing();
  const canSeeGiving = useCanSeeGiving();

  const hasFunds = canSeeGiving && hasFundIt;

  const firstVisibleLink = useMemo(() => {
    if (canViewAllGivingData) return "/giving";
    if (canAddGifts) return "/giving/input";
    if (canSeeCampaignsNav) return "/campaigns";
    return null;
  }, [canViewAllGivingData, canAddGifts, canSeeCampaignsNav]);

  return (
    <SNavSection
      $hasFunds={hasFunds}
      label="Giving"
      data-cy="NavBar_GivingMenu"
      icon="money-bill-wave far"
      color={navColors.giving}
      navSectionKey={navSectionKeys.giving}
      SettingsContent={canSeeSettings ? SettingsContent : undefined}
      settingsSelected={settingsSelected}
      headerTextRef={givingRef}
      isSquiggleFlashing={isGivingSquiggleFlashing}
      defaultRoute={firstVisibleLink}
      HeaderSuffixComponent={GivingHeaderBadge}
    >
      <NavLinksWrapper minWidth={110}>
        {canViewAllGivingData && (
          <>
            <NavLink
              navSectionKey={navSectionKeys.giving}
              to="giving"
              data-cy="NavBar_Giving"
              icon="fa-solid fa-chart-pie-simple-circle-dollar"
              ref={givingSummaryRef}
              isFlashing={isGivingSummaryFlashing}
            >
              Designations
            </NavLink>
            <NavLink
              to="giving/batch"
              data-cy="NavBar_BatchesPage"
              icon="fa-solid fa-money-bills"
              navSectionKey={navSectionKeys.giving}
            >
              Batches
            </NavLink>
          </>
        )}
        {canAddGifts && (
          <NavLink
            to="giving/input"
            data-cy="NavBar_AddGiftsPage"
            icon="circle-dollar-to-slot fas"
            navSectionKey={navSectionKeys.giving}
            ref={addGiftsLinkRef}
            isFlashing={isAddGiftsFlashing}
          >
            Record Gifts
          </NavLink>
        )}
        {canSeeCampaignsNav && (
          <NavLink
            to="campaigns"
            data-cy="NavBar_Campaigns"
            icon="fa-solid fa-temperature-list"
            navSectionKey={navSectionKeys.giving}
          >
            Campaigns
          </NavLink>
        )}
      </NavLinksWrapper>
      <OngoingCampaigns />
      {hasFunds && (
        <>
          <NavSectionDivider />
          <Funds />
        </>
      )}
    </SNavSection>
  );
};

const SNavSection = styled(NavSection)`
  ${({ $hasFunds }) => ($hasFunds ? "" : "padding-bottom: 4px;")}
`;

export default LegacyGiving;
